import { ButtonLink, Card, DataTable, Spinner } from "@components/ui-kit";
import { Pagination } from "@components/ui-kit/table/pagination";
import { Trans, t } from "@lingui/macro";
import { ExpensesWhereColumn } from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { can, withCan } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FunctionComponent, useState } from "react";
import { PageExpensesCtx, useData } from "./PageExpenses";
import { StoreExpenses } from "./StoreExpenses";
import { TableExpenses } from "./table/TableExpenses";

type TableModuleExpensesProps = {
  contractorId?: string | string[];
  bordered?: boolean;
};

export const TableModuleExpenses: FunctionComponent<
  React.PropsWithChildren<TableModuleExpensesProps>
> = withCan(
  { do: ["expense_read_own", "expense_read_all", "expense_read_team"] },
  observer(function TableModuleExpenses({ contractorId, bordered = true }) {
    const [store] = useState(() => {
      const store = StoreExpenses.create();
      store.selectable = false;
      store.asExternalModule = true;
      store.hideColsInExternalModule = ["contractor"];

      store.where.filtersByColumn
        .get(ExpensesWhereColumn.ContractorId)!
        .setValue(contractorId || [], { skipOnChange: true });
      return store;
    });

    const { loading } = useData(store);

    return (
      <PageExpensesCtx.Provider value={store}>
        <div data-testid="card-list-projects">
          {loading && <Spinner absolute />}
          <Card border={bordered ? undefined : "none"}>
            <div className="h-24 flex items-center justify-between px-6">
              <div className="flex items-center h-full">
                <h2 className="text-3xl font-bold mr-16">
                  <Trans>Expenses</Trans>
                </h2>

                <DataTable.Filter>
                  <FiltersPanel<ExpensesWhereColumn>
                    searchPlaceholder={t`Search by type, project/s, var. symbol, IBAN`}
                    onSearch={(val) => {
                      store.searchTerm = val;
                      store.pagination.resetPage();
                    }}
                  />
                </DataTable.Filter>
              </div>
              <div className="flex items-center">
                <div className="px-2">
                  {can("expense_create") && (
                    <ButtonLink
                      href={{
                        pathname: "/expenses/create",
                        query: { contractorId },
                      }}
                      leftIcon={<Icon name="plus" />}
                    >{t`New Expense`}</ButtonLink>
                  )}
                </div>
              </div>
            </div>

            <div data-testid="table">
              <TableExpenses />
              {!loading && <Pagination store={store.pagination} />}
            </div>
          </Card>
        </div>
      </PageExpensesCtx.Provider>
    );
  }),
);
